import cx from 'classnames'
import { CallTypeEnum } from 'features/calls/types'
import { getClaimCallPath } from 'features/claims/utils/claimsPathUtils'
import { useEffect, useState } from 'react'
import { Accordion } from 'react-bootstrap'
import { FaAngleDown, FaAngleRight, FaPhoneVolume } from 'react-icons/fa'
import { useNavigate } from 'react-router-dom'
import { getTntIdForNavigation } from 'utils/tenantUrlUtils'
import { Button, ObModal } from '@outbound-ai/ursa'
import styled from 'styled-components'

// components
// assets
// utils
interface IProps {
  claimId: string
  callId: string
  type: CallTypeEnum
}

export const ClaimCallJobStartedModal = ({ claimId, type, callId }: IProps) => {
  // utils
  const navigate = useNavigate()
  // local state
  const [show, setShow] = useState(true)
  const [isExpanded, setIsExpanded] = useState(false)

  // When a new call starts we want to make sure that we did not hide this modal previousliy
  useEffect(() => {
    if (!show) setShow(true)
  }, [callId])

  // handlers
  const handleToggleDetails = () => {
    setIsExpanded((state) => !state)
  }

  const handleViewLiveCall = () => redirectToClaimCall(claimId, callId)

  const redirectToClaimCall = (oaiClaimId: string, jobId: string) => {
    const tenantId = getTntIdForNavigation()
    navigate(getClaimCallPath(tenantId, oaiClaimId, jobId))
  }

  // variables
  const modalContent = {
    [CallTypeEnum.humanAgent]: {
      title: 'Contacting a Payer Agent',
      headerText: 'Outbound’s AI agent is now conducting a call to the payer.',
      detailsSummary: 'Learn more',
      detailsText:
        'During a payer agent call, Outbound’s AI agent will navigate the IVR menus and wait on hold on your behalf.  You can takeover the call at any time, removing the AI agent from the conversation. You can also minimize the call view at any time to continue accessing other areas of the app.',
      extraText: "We'll let you know when the payer rep is on the line and ready to talk.",
      closeButtonText: 'Minimize Call',
    },
    [CallTypeEnum.unattended]: {
      title: 'IVR Call in Background',
      headerText: 'Outbound’s AI agent is now conducting a call to the payer.',
      detailsSummary: 'What’s Next',
      detailsText:
        'Once the call is complete, you can review it and any gathered insights in the call summary. Any change to the claim will be visible in the claim history panel, and all changes are editable and will be highlighted in blue.',
      extraText: 'No further action is required at this time.',
      closeButtonText: 'Done',
    },
    [CallTypeEnum.attendable]: {},
    [CallTypeEnum.standard]: {},
    [CallTypeEnum.diagnosticHeadset]: {},
  }[type]

  const isViewableCall = [CallTypeEnum.attendable, CallTypeEnum.humanAgent].includes(type)
  return (
    <ObModal
      show={show}
      close={() => setShow(false)}
      size="lg"
      title={modalContent.title}
      height="auto"
      hideCloseButton={true}
      modalFooter={
        <FooterStyle>
          <Button
            variant="secondary"
            radius="rounded"
            size="large"
            width="100%"
            onClick={handleViewLiveCall}
            className="take-ownership-btn"
            disabled={!isViewableCall}
          >
            View Live Call
          </Button>

          <Button variant="primary" radius="rounded" size="large" width="100%" onClick={() => setShow(false)}>
            {modalContent.closeButtonText}
          </Button>
        </FooterStyle>
      }
    >
      <Div>
        <div className="section-header">
          <FaPhoneVolume />
          {modalContent.headerText}
        </div>

        <StyledAccordion flush>
          <Accordion.Item eventKey="0">
            <Accordion.Header onClick={handleToggleDetails} className={cx({ expanded: isExpanded })}>
              {modalContent.detailsSummary} {isExpanded ? <FaAngleDown /> : <FaAngleRight />}
            </Accordion.Header>
            <Accordion.Body>{modalContent.detailsText}</Accordion.Body>
          </Accordion.Item>
        </StyledAccordion>

        <p className={cx({ 'text-center': !isViewableCall })}>{modalContent.extraText}</p>
      </Div>
    </ObModal>
  )
}

const Div = styled.div`
  padding: 1.5rem 2.5rem;

  .section-header {
    display: flex;
    align-items: center;
    gap: 1.5rem;
    padding: 0.5rem;
    color: ${({ theme }) => theme.colors.grey.darker};
    font-weight: 500;
    font-size: 20px;

    svg {
      color: ${({ theme }) => theme.colors.secondary.darker};
      width: ${({ theme }) => theme.util.pixelToRem(36)};
      height: ${({ theme }) => theme.util.pixelToRem(48)};
    }
  }

  p {
    ${({ theme }) => theme.typography.m.normal};
  }
`

const FooterStyle = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  width: -webkit-fill-available;
`

const StyledAccordion = styled(Accordion)`
  border-radius: ${({ theme }) => theme.borderRadius.rounded};
  overflow: hidden;
  color: ${({ theme }) => theme.colors.info.darker};
  margin-block: 0.625rem;

  .accordion-header {
    &:hover,
    &:active,
    &:focus {
      border: 0;
      outline: none;
      box-shadow: none;
    }
    .accordion-button {
      display: flex;
      align-items: center;
      gap: 0.25rem;
      background-color: inherit;
      font-weight: ${({ theme }) => theme.fontWeight.medium};
      font-size: 12px;
      line-height: 160%;
      background: ${({ theme }) => theme.colors.info.lighter}66;
      color: inherit;
      padding: 0.125rem 1rem;
      border: 0;
      outline: none;
      box-shadow: none;

      &:hover,
      &:active,
      &:focus {
        border: 0;
        outline: none;
        box-shadow: none;
      }

      &::after {
        content: none;
      }
    }

    &.expanded .accordion-button {
      padding-block: 0.5rem 0;
    }
  }

  .accordion-body {
    padding-top: 0.25rem;
    padding-inline: 1rem;
    font-weight: ${({ theme }) => theme.fontWeight.normal};
    ${({ theme }) => theme.typography.s.normal};
    background: ${({ theme }) => theme.colors.info.lighter}66;

    &:hover,
    &:active,
    &:focus {
      border: 0;
      outline: none;
      box-shadow: none;
    }
  }
`
